import NameTranslation from "../../core/model/translations/NameTranslation";
import { IHttpService } from "../../core/service/HttpService";
import GetAllIndexTypeGroupsForCategoryInteractor from "../../indexTypes/interactors/GetAllIndexTypeGroupsForCategory";
import GetAllIndexTypesInteractor from "../../indexTypes/interactors/GetAllIndexTypesInteractor";
import { IIndexTypeGroup } from "../../indexTypes/model/IndexTypeGroup";
import { IOrganisationIndex } from "../model/OrganisationIndex";
import OrganisationIndexCategory from "../model/OrganisationIndexCategory";
import { IOrganisationIndexGroup } from "../model/OrganisationIndexGroup";
import ConnectIndexesInteractor from "./ConnectIndexesInteractor";
import ConnectIndexGroupsInteractor from "./ConnectIndexGroupsInteractor";
import CreateOrganisationIndexCategoryInteractor from "./CreateOrganisationIndexCategoryInteractor";
import CreateOrganisationIndexGroupInteractor from "./CreateOrganisationIndexGroupInteractor";
import CreateOrganisationIndexInteractor from "./CreateOrganisationIndexInteractor";
import GetIndexTypeGroupTreeInteractor from "./GetIndexTypeGroupTreeInteractor";
import ImportOrganisationDescriptorsInteractor, {
  ResultItem,
  TResultItem,
} from "./ImportOrganisationDescriptorsInteractor";

export interface TGroupImportConfig {
  name: string;
  type: string;
  importOptions?: {
    url: string;
    sheet: string;
    ORG_KEY: string;
    DEFAULT_NAME_KEY: string;
  };
  children?: TGroupImportConfig[];
}

let defaultKeys = {
  DEFAULT_NAME_KEY: "C",
  ORG_KEY: undefined,
  TYPE_KEY: "A",
  PARENT_KEY: "B",
};

export type OrganisationImportResult = {
  [organisationId: number]: TResultItem[];
};

export interface TOrganisationImportConfig {
  id: number;
  groups: TGroupImportConfig[];
}
export default class ImportAllOrganisationCategoriesInteractor {
  private getIndexTypeGroupTree: GetIndexTypeGroupTreeInteractor;
  private createOrganisationIndexGroup: CreateOrganisationIndexGroupInteractor;
  private getAllIndexTypeGroupsForCategory: GetAllIndexTypeGroupsForCategoryInteractor;
  private getAllIndexTypes: GetAllIndexTypesInteractor;
  private createOrganisationIndex: CreateOrganisationIndexInteractor;
  private httpService: IHttpService;
  private createOrganisationIndexCategory: CreateOrganisationIndexCategoryInteractor;
  private importOrganisationDescriptors: ImportOrganisationDescriptorsInteractor;
  private connectIndexes: ConnectIndexesInteractor;
  private connectIndexGroups: ConnectIndexGroupsInteractor;

  constructor({
    getIndexTypeGroupTree,
    createOrganisationIndexGroup,
    getAllIndexTypeGroupsForCategory,
    getAllIndexTypes,
    createOrganisationIndex,
    httpService,
    createOrganisationIndexCategory,
    importOrganisationDescriptors,
    connectIndexes,
    connectIndexGroups,
  }: any) {
    this.getIndexTypeGroupTree = getIndexTypeGroupTree;
    this.createOrganisationIndexGroup = createOrganisationIndexGroup;
    this.getAllIndexTypeGroupsForCategory = getAllIndexTypeGroupsForCategory;
    this.getAllIndexTypes = getAllIndexTypes;
    this.createOrganisationIndex = createOrganisationIndex;
    this.httpService = httpService;
    this.createOrganisationIndexCategory = createOrganisationIndexCategory;
    this.importOrganisationDescriptors = importOrganisationDescriptors;
    this.connectIndexes = connectIndexes;
    this.connectIndexGroups = connectIndexGroups;
  }

  private async createGroup(
    item: TGroupImportConfig,
    organisationId: number,
    languageId: number,
    category: any,
    groupTypes: IIndexTypeGroup[],
    parentId?: number
  ) {
    return this.createOrganisationIndexGroup.execute({
      name: item.name,
      nameTranslations: [
        {
          organisationId: organisationId,
          languageId,
          nameTranslation: item.name,
          valid: true,
        },
      ],
      organisationIndexCategoryId: category.id,
      organisationId: organisationId,
      indexTypeGroupId:
        groupTypes.find((g) => g.name.toLowerCase() === item.type.toLowerCase())
          ?.id || -1,
      parentId: parentId || null,
      valid: true,
    } as any);
  }

  async execute({
    indexTypeCategoryId,
    baseUrl,
    organisations,
    categoryName,
    languageId,
    debug = false,
  }: {
    indexTypeCategoryId: number;
    categoryName: string;
    baseUrl: string;
    organisations: TOrganisationImportConfig[];
    languageId: number;
    debug?: boolean;
  }) {
    let importResult: OrganisationImportResult = {};
    const groupTypes = await this.getAllIndexTypeGroupsForCategory.execute(
      indexTypeCategoryId
    );

    for (let organisation of organisations) {
      let organisationId = organisation.id;
      let category = await this.createOrganisationIndexCategory.execute(
        OrganisationIndexCategory({
          id: -1,
          indexTypeCategoryId,
          name: categoryName,
          organisationId,
          valid: true,
          nameTranslations: [
            NameTranslation({
              id: -1,
              nameTranslation: categoryName,
              organisationId,
              languageId,
              valid: true,
            }),
          ],
        })
      );

      debug &&
        console.log(
          `Created category "${categoryName}":${category.id} for organisation ${organisation.id}`
        );
      let i: number = 0;
      for (let group of organisation.groups) {
        let root = await this.createGroup(
          group,
          organisationId,
          languageId,
          category,
          groupTypes
        );
        let rootObj = ResultItem(
          root,
          root.name,
          root.type,
          root.name,
          baseUrl + root.importOptions?.url + i.toString()
        );
        rootObj.group = root;
        rootObj.id = root.id;
        importResult[organisationId] = [
          ...(importResult[organisationId] || []),
          ...[rootObj],
        ];
        debug && console.log(`Created group "${group.name}":${root.id}`);
        debug && console.table(root);
        if (group.children) {
          let j: number = 0;
          for (let item of group.children) {
            let newGroup = await this.createGroup(
              item,
              organisationId,
              languageId,
              category,
              groupTypes,
              root.id
            );
            let obj = ResultItem(
              item,
              item.name,
              item.type,
              item.name,
              baseUrl +
                item.importOptions?.url +
                i.toString() +
                "-" +
                j.toString()
            );
            obj.group = newGroup;
            obj.id = newGroup.id;
            importResult[organisationId] = [
              ...(importResult[organisationId] || []),
              ...[obj],
            ];

            debug &&
              console.log(`Created child group "${item.name}":${newGroup.id}`);
            debug && console.table(newGroup);

            if (item.importOptions) {
              let result = await this.importDescriptors(
                category,
                organisationId,
                languageId,
                baseUrl,
                item,
                debug,
                newGroup.id
              );
              importResult[organisationId] = [
                ...(importResult[organisationId] || []),
                ...result,
              ];
            }
            j++;
          }
        }
        i++;
      }
    }

    //connect indexes
    for (let organisation of organisations) {
      let orgResults = importResult[organisation.id];

      if (orgResults) {
        for (let result of orgResults) {
          await this.connect(importResult, organisation, result);
        }
      }
    }

    return importResult;
  }

  private async connect(
    importResult: OrganisationImportResult,
    organisation: TOrganisationImportConfig,
    result: TResultItem
  ) {
    let candidates: TResultItem[] = [];
    for (let orgId in importResult) {
      if (organisation.id !== Number.parseInt(orgId, 10)) {
        candidates = [
          ...candidates,
          ...this.findReferenceCandidates(
            importResult[orgId],
            result.referenceId
          ),
        ];
      }
    }
    if (candidates && candidates.length > 0) {
      if (result.index) {
        let connections = candidates
          .map((item) => item.index)
          .filter((item) => item !== undefined) as any as IOrganisationIndex[];
        await this.connectIndexes.execute(
          result.index.id,
          connections,
          organisation.id,
          result.defaultName
        );
      } else if (result.group) {
        let connections = candidates
          .map((item) => item.group)
          .filter(
            (item) => item !== undefined
          ) as any as IOrganisationIndexGroup[];
        await this.connectIndexGroups.execute(
          result.group.id,
          connections,
          organisation.id,
          result.defaultName
        );
      }
    }
    if (result.children && result.children.length > 0) {
      for (let child of result.children) {
        await this.connect(importResult, organisation, child);
      }
    }
  }

  private findReferenceCandidates(
    otherItems: TResultItem[],
    referenceId: string
  ) {
    return otherItems
      .map((item) => item.findByReferenceId(referenceId))
      .flat()
      .filter((item) => item !== undefined) as any as TResultItem[];
  }

  private async importDescriptors(
    category: any,
    organisationId: number,
    languageId: number,
    baseUrl: string,
    item: TGroupImportConfig,
    debug: boolean,
    groupId: any
  ) {
    if (!item.importOptions) {
      throw new Error("importOptions must be defined");
    }
    return this.importOrganisationDescriptors.execute({
      categoryId: category.id,
      organisationId,
      dryRun: false,
      languageId,
      url: baseUrl + item.importOptions.url,
      debug,
      indexGroupId: groupId,
      importOptions: {
        sheet: item.importOptions.sheet,
        keys: Object.assign({}, defaultKeys, {
          ORG_KEY: item.importOptions.ORG_KEY,
          DEFAULT_NAME_KEY:
            item.importOptions.DEFAULT_NAME_KEY || defaultKeys.DEFAULT_NAME_KEY,
        }),
      },
    });
  }
}
